import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Calendar,
  Building,
  Shield,
  Clock,
  BarChart3,
  Loader2,
  ArrowLeft,
  RefreshCw,
  LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import api from '@/services/api';
import InsightCard from './InsightCard';
import ReportTable from './ReportTable';
import ReportChart from './ReportChart';

interface InsightReportsProps {
  onBack: () => void;
}

interface InsightItem {
  id: InsightType;
  title: string;
  description: string;
  icon: LucideIcon;
  color: string;
  badge?: string;
}

interface InsightCategory {
  title: string;
  color: string;
  insights: InsightItem[];
}

type InsightType = 
  | 'cost-per-employee'
  | 'repair-cost-ratio'
  | 'tco'
  | 'budget-forecast'
  | 'lifecycle'
  | 'monthly-trends'
  | 'branch-comparison'
  | 'warranty-coverage'
  | 'unmarked-assets'
  | 'audit-compliance'
  | 'age-distribution'
  | null;

const insightCategories: InsightCategory[] = [
  {
    title: 'Financial Insights',
    color: 'text-green-600',
    insights: [
      {
        id: 'cost-per-employee',
        title: 'Cost per Employee',
        description: 'Total asset value assigned per employee',
        icon: DollarSign,
        color: 'bg-green-500',
      },
      {
        id: 'repair-cost-ratio' as const,
        title: 'Repair Cost Ratio',
        description: 'Flag assets where repairs exceed purchase cost',
        icon: AlertTriangle,
        color: 'bg-red-500',
        badge: 'Critical',
      },
      {
        id: 'tco' as const,
        title: 'Total Cost of Ownership',
        description: 'Purchase + repairs + maintenance costs',
        icon: TrendingUp,
        color: 'bg-emerald-500',
      },
      {
        id: 'budget-forecast' as const,
        title: 'Budget Forecast',
        description: 'Predict replacement costs based on depreciation',
        icon: Calendar,
        color: 'bg-blue-500',
      },
    ],
  },
  {
    title: 'Operational Insights',
    color: 'text-blue-600',
    insights: [
      {
        id: 'lifecycle' as const,
        title: 'Asset Lifecycle',
        description: 'Average lifespan by category before disposal',
        icon: Clock,
        color: 'bg-indigo-500',
      },
      {
        id: 'monthly-trends' as const,
        title: 'Monthly Trends',
        description: 'Repairs, acquisitions, and issues over time',
        icon: BarChart3,
        color: 'bg-purple-500',
      },
      {
        id: 'branch-comparison' as const,
        title: 'Branch Comparison',
        description: 'Compare performance across branches',
        icon: Building,
        color: 'bg-orange-500',
      },
    ],
  },
  {
    title: 'Compliance Insights',
    color: 'text-amber-600',
    insights: [
      {
        id: 'warranty-coverage' as const,
        title: 'Warranty Coverage',
        description: 'Assets at risk without warranty protection',
        icon: Shield,
        color: 'bg-amber-500',
        badge: 'Risk',
      },
      {
        id: 'unmarked-assets' as const,
        title: 'Unmarked Assets',
        description: 'Assets missing proper marking/engraving',
        icon: AlertTriangle,
        color: 'bg-rose-500',
      },
      {
        id: 'audit-compliance' as const,
        title: 'Audit Compliance',
        description: 'Assets not audited within threshold period',
        icon: Clock,
        color: 'bg-yellow-500',
      },
      {
        id: 'age-distribution' as const,
        title: 'Asset Age Distribution',
        description: 'Breakdown of asset ages across fleet',
        icon: BarChart3,
        color: 'bg-cyan-500',
      },
    ],
  },
];

export default function InsightReports({ onBack }: InsightReportsProps) {
  const [selectedInsight, setSelectedInsight] = useState<InsightType>(null);

  const { data: insightData, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['insight', selectedInsight],
    queryFn: async () => {
      if (!selectedInsight) return null;
      const response = await api.get(`/reports/insights/${selectedInsight}`);
      return response.data.data;
    },
    enabled: !!selectedInsight,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const renderInsightContent = () => {
    if (!selectedInsight || !insightData) return null;

    switch (selectedInsight) {
      case 'cost-per-employee':
        return <CostPerEmployeeInsight data={insightData} formatCurrency={formatCurrency} />;
      case 'repair-cost-ratio':
        return <RepairCostRatioInsight data={insightData} formatCurrency={formatCurrency} />;
      case 'tco':
        return <TCOInsight data={insightData} formatCurrency={formatCurrency} />;
      case 'budget-forecast':
        return <BudgetForecastInsight data={insightData} formatCurrency={formatCurrency} />;
      case 'lifecycle':
        return <LifecycleInsight data={insightData} />;
      case 'monthly-trends':
        return <MonthlyTrendsInsight data={insightData} formatCurrency={formatCurrency} />;
      case 'branch-comparison':
        return <BranchComparisonInsight data={insightData} formatCurrency={formatCurrency} />;
      case 'warranty-coverage':
        return <WarrantyCoverageInsight data={insightData} formatCurrency={formatCurrency} />;
      case 'unmarked-assets':
        return <UnmarkedAssetsInsight data={insightData} formatCurrency={formatCurrency} />;
      case 'audit-compliance':
        return <AuditComplianceInsight data={insightData} />;
      case 'age-distribution':
        return <AgeDistributionInsight data={insightData} />;
      default:
        return null;
    }
  };

  const currentInsight = insightCategories
    .flatMap(c => c.insights)
    .find(i => i.id === selectedInsight);

  if (selectedInsight) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-4">
              {currentInsight && (
                <>
                  <div className={cn('w-12 h-12 rounded-lg flex items-center justify-center', currentInsight.color)}>
                    <currentInsight.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle>{currentInsight.title}</CardTitle>
                    <CardDescription>{currentInsight.description}</CardDescription>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshCw className={cn('w-4 h-4 mr-2', isFetching && 'animate-spin')} />
                Refresh
              </Button>
              <Button variant="outline" onClick={() => setSelectedInsight(null)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
          </CardHeader>
        </Card>

        {isLoading ? (
          <Card>
            <CardContent className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="flex items-center justify-center h-64 text-destructive">
              Failed to load insight data
            </CardContent>
          </Card>
        ) : (
          renderInsightContent()
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Business Insights</h2>
          <p className="text-muted-foreground">Deep analytics for informed decision-making</p>
        </div>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Reports
        </Button>
      </div>

      {insightCategories.map((category, idx) => (
        <div key={idx} className="space-y-4">
          <h3 className={cn('text-lg font-semibold', category.color)}>{category.title}</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {category.insights.map((insight) => (
              <InsightCard
                key={insight.id}
                title={insight.title}
                description={insight.description}
                icon={insight.icon}
                color={insight.color}
                badge={insight.badge}
                onClick={() => setSelectedInsight(insight.id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Individual Insight Components

function CostPerEmployeeInsight({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Employees</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalEmployees || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>With Assets</CardDescription>
            <CardTitle className="text-3xl text-green-500">{data.summary?.employeesWithAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Asset Value</CardDescription>
            <CardTitle className="text-2xl">{formatCurrency(data.summary?.totalAssetValue || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Value/Employee</CardDescription>
            <CardTitle className="text-2xl">{formatCurrency(data.summary?.avgValuePerEmployee || 0)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Top Asset Holders by Value</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.topHolders || []}
            columns={[
              { key: 'employeeId', header: 'ID' },
              { key: 'name', header: 'Name' },
              { key: 'department', header: 'Department' },
              { key: 'assetCount', header: 'Assets', className: 'text-right' },
              { key: 'totalPurchaseValue', header: 'Total Value', className: 'text-right', render: (v) => formatCurrency(v) },
            ]}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Value by Department</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart 
            type="bar" 
            data={data.byDepartment?.map((d: any) => ({ name: d.name, value: d.totalValue })) || []} 
            height={300}
            title="Value"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function RepairCostRatioInsight({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardDescription>Critical (75%+ repair ratio)</CardDescription>
            <CardTitle className="text-3xl text-red-500">{data.summary?.criticalCount || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardDescription>Warning (50-75%)</CardDescription>
            <CardTitle className="text-3xl text-amber-500">{data.summary?.warningCount || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Repair Spend</CardDescription>
            <CardTitle className="text-2xl">{formatCurrency(data.summary?.totalRepairSpend || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Repair Ratio</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.avgRepairRatio?.toFixed(1) || 0}%</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Critical Assets - Consider Replacement
          </CardTitle>
          <CardDescription>Assets where repair costs exceed 75% of purchase cost</CardDescription>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.criticalAssets || []}
            columns={[
              { key: 'assetTag', header: 'Asset Tag' },
              { key: 'name', header: 'Name' },
              { key: 'category', header: 'Category' },
              { key: 'purchaseCost', header: 'Purchase Cost', className: 'text-right', render: (v) => formatCurrency(v) },
              { key: 'totalRepairCost', header: 'Repair Cost', className: 'text-right text-red-500', render: (v) => formatCurrency(v) },
              { key: 'repairRatio', header: 'Ratio', className: 'text-right font-bold text-red-500', render: (v) => `${v}%` },
            ]}
            emptyMessage="No critical assets found"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function TCOInsight({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Assets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Purchase Cost</CardDescription>
            <CardTitle className="text-xl">{formatCurrency(data.summary?.totalPurchaseCost || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Repair Cost</CardDescription>
            <CardTitle className="text-xl text-orange-500">{formatCurrency(data.summary?.totalRepairCost || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Maintenance Cost</CardDescription>
            <CardTitle className="text-xl text-blue-500">{formatCurrency(data.summary?.totalMaintenanceCost || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-2 border-primary">
          <CardHeader className="pb-2">
            <CardDescription>Total TCO</CardDescription>
            <CardTitle className="text-xl text-primary">{formatCurrency(data.summary?.totalTCO || 0)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">TCO by Category</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart 
            type="bar" 
            data={data.byCategory?.map((c: any) => ({ name: c.name, value: c.totalTCO })) || []} 
            height={300}
            title="TCO"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Most Costly Assets (Lifetime)</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.topCostlyAssets || []}
            columns={[
              { key: 'assetTag', header: 'Asset Tag' },
              { key: 'name', header: 'Name' },
              { key: 'category', header: 'Category' },
              { key: 'ageYears', header: 'Age (yrs)', className: 'text-right' },
              { key: 'purchaseCost', header: 'Purchase', className: 'text-right', render: (v) => formatCurrency(v) },
              { key: 'repairCosts', header: 'Repairs', className: 'text-right', render: (v) => formatCurrency(v) },
              { key: 'totalCost', header: 'Total TCO', className: 'text-right font-bold', render: (v) => formatCurrency(v) },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function BudgetForecastInsight({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  const chartData = data.forecast?.map((f: any) => ({
    name: f.year.toString(),
    value: f.replacementCost,
  })) || [];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Assets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Forecasted Cost (3 years)</CardDescription>
            <CardTitle className="text-2xl">{formatCurrency(data.summary?.totalForecastedCost || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Annual Replacements</CardDescription>
            <CardTitle className="text-3xl">{Math.round(data.summary?.avgAnnualReplacement || 0)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Replacement Cost Forecast</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart type="bar" data={chartData} height={300} title="Replacement Cost" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Yearly Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.forecast || []}
            columns={[
              { key: 'year', header: 'Year' },
              { key: 'assetsToReplace', header: 'Assets to Replace', className: 'text-right' },
              { key: 'replacementCost', header: 'Estimated Cost', className: 'text-right', render: (v) => formatCurrency(v) },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function LifecycleInsight({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Disposed Assets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalDisposed || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Average Lifespan</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.avgLifespan?.toFixed(1) || 0} years</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lifespan by Category</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart 
            type="horizontal-bar" 
            data={data.byCategory?.map((c: any) => ({ name: c.category, value: c.avgLifespan })) || []} 
            height={300}
            title="Avg Years"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Category Details</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.byCategory || []}
            columns={[
              { key: 'category', header: 'Category' },
              { key: 'totalDisposed', header: 'Disposed', className: 'text-right' },
              { key: 'avgLifespan', header: 'Avg Lifespan', className: 'text-right', render: (v) => `${v} years` },
              { key: 'shortestLife', header: 'Shortest', className: 'text-right', render: (v) => `${v} years` },
              { key: 'longestLife', header: 'Longest', className: 'text-right', render: (v) => `${v} years` },
            ]}
            emptyMessage="No disposed assets found"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function MonthlyTrendsInsight({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Repairs</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalRepairs || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Repair Cost</CardDescription>
            <CardTitle className="text-xl">{formatCurrency(data.summary?.totalRepairCost || 0)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Consumables Issued</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalIssues || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>New Acquisitions</CardDescription>
            <CardTitle className="text-3xl text-green-500">{data.summary?.totalAcquisitions || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Acquisition Cost</CardDescription>
            <CardTitle className="text-xl">{formatCurrency(data.summary?.totalAcquisitionCost || 0)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Repairs Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart 
            type="line" 
            data={data.trends?.map((t: any) => ({ name: t.month, value: t.repairs })) || []} 
            height={300}
            title="Repairs"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Acquisitions Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart 
            type="line" 
            data={data.trends?.map((t: any) => ({ name: t.month, value: t.acquisitions })) || []} 
            height={300}
            title="Acquisitions"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function BranchComparisonInsight({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Branches</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalBranches || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Utilization</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.avgUtilization || 0}%</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Highest Utilization</CardDescription>
            <CardTitle className="text-xl text-green-500">
              {data.summary?.highestUtilization?.branch || '-'} ({data.summary?.highestUtilization?.rate || 0}%)
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Utilization by Branch</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart 
            type="horizontal-bar" 
            data={data.branches?.map((b: any) => ({ name: b.name, value: b.utilizationRate })) || []} 
            height={300}
            title="Utilization %"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Branch Details</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.branches || []}
            columns={[
              { key: 'name', header: 'Branch' },
              { key: 'totalAssets', header: 'Assets', className: 'text-right' },
              { key: 'employeeCount', header: 'Employees', className: 'text-right' },
              { key: 'utilizationRate', header: 'Utilization', className: 'text-right', render: (v) => `${v}%` },
              { key: 'assetsPerEmployee', header: 'Assets/Employee', className: 'text-right' },
              { key: 'totalValue', header: 'Total Value', className: 'text-right', render: (v) => formatCurrency(v) },
              { key: 'repairCount', header: 'Repairs', className: 'text-right' },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function WarrantyCoverageInsight({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  const coverageData = [
    { name: 'Covered', value: data.summary?.covered || 0 },
    { name: 'Expiring Soon', value: data.summary?.expiringSoon || 0 },
    { name: 'Expired', value: data.summary?.expired || 0 },
    { name: 'No Warranty', value: data.summary?.noWarranty || 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Assets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardDescription>Covered</CardDescription>
            <CardTitle className="text-3xl text-green-500">{data.summary?.covered || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardDescription>Expiring Soon</CardDescription>
            <CardTitle className="text-3xl text-amber-500">{data.summary?.expiringSoon || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardDescription>Expired</CardDescription>
            <CardTitle className="text-3xl text-red-500">{data.summary?.expired || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Value at Risk</CardDescription>
            <CardTitle className="text-xl text-red-500">{formatCurrency(data.summary?.valueAtRisk || 0)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Coverage Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ReportChart type="pie" data={coverageData} height={280} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Expiring Within 30 Days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReportTable
              data={data.expiringSoon || []}
              columns={[
                { key: 'assetTag', header: 'Asset Tag' },
                { key: 'name', header: 'Name' },
                { key: 'daysLeft', header: 'Days Left', className: 'text-right text-amber-500 font-bold' },
              ]}
              emptyMessage="No warranties expiring soon"
              maxHeight="max-h-[200px]"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function UnmarkedAssetsInsight({ data, formatCurrency }: { data: any; formatCurrency: (v: number) => string }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Assets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardDescription>Unmarked</CardDescription>
            <CardTitle className="text-3xl text-red-500">{data.summary?.totalUnmarked || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Marking Rate</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.markingRate || 0}%</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Unmarked Value</CardDescription>
            <CardTitle className="text-xl">{formatCurrency(data.summary?.valueUnmarked || 0)}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Unmarked Assets by Category</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart 
            type="bar" 
            data={data.byCategory?.map((c: any) => ({ name: c.name, value: c.count })) || []} 
            height={300}
            title="Count"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Unmarked Assets List</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.assets || []}
            columns={[
              { key: 'assetTag', header: 'Asset Tag' },
              { key: 'name', header: 'Name' },
              { key: 'category', header: 'Category' },
              { key: 'branch', header: 'Branch' },
              { key: 'assignedTo', header: 'Assigned To' },
              { key: 'purchaseCost', header: 'Value', className: 'text-right', render: (v) => formatCurrency(v || 0) },
            ]}
            maxHeight="max-h-[400px]"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function AuditComplianceInsight({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Assets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardDescription>Compliant</CardDescription>
            <CardTitle className="text-3xl text-green-500">{data.summary?.compliant || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardDescription>Overdue</CardDescription>
            <CardTitle className="text-3xl text-red-500">{data.summary?.overdue || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Compliance Rate</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.complianceRate || 0}%</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Compliance by Branch</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart 
            type="horizontal-bar" 
            data={data.byBranch?.map((b: any) => ({ name: b.name, value: b.complianceRate })) || []} 
            height={300}
            title="Compliance %"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Overdue for Audit
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.overdueAssets || []}
            columns={[
              { key: 'assetTag', header: 'Asset Tag' },
              { key: 'name', header: 'Name' },
              { key: 'category', header: 'Category' },
              { key: 'branch', header: 'Branch' },
              { key: 'daysSinceAudit', header: 'Days Since Audit', className: 'text-right text-red-500', render: (v) => v || 'Never' },
            ]}
            maxHeight="max-h-[400px]"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function AgeDistributionInsight({ data }: { data: any }) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Assets</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.totalAssets || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Average Age</CardDescription>
            <CardTitle className="text-3xl">{data.summary?.avgAgeYears || 0} years</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Oldest Asset</CardDescription>
            <CardTitle className="text-lg">
              {data.summary?.oldestAsset?.name || '-'}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Age Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportChart type="bar" data={data.distribution || []} height={300} title="Assets" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Distribution Details</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportTable
            data={data.distribution || []}
            columns={[
              { key: 'name', header: 'Age Range' },
              { key: 'value', header: 'Count', className: 'text-right' },
              { key: 'percentage', header: 'Percentage', className: 'text-right', render: (v) => `${v}%` },
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}
